/**
 * Attendances API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for Attendances with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  AttendanceCreate,
  AttendanceCreateInput,
  AttendanceFilters,
  AttendanceListResponse,
  AttendanceResponse,
  AttendanceUpdate,
  AttendanceUpdateInput,
} from "@/types/attendance"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of attendances with permission checks
 */
export function useAttendances(filters: AttendanceFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["attendances", filters],
    queryFn: async (): Promise<AttendanceListResponse> => {
      const response = await api.attendances.readAttendancesApiV1AttendancesGet(
        {
          limit: filters.limit,
          skip: filters.skip,
          search: filters.search,
          sortBy: filters.sort_by,
          sortOrder: filters.sort_order,
          createdBy: filters.created_by,
          createdAfter: filters.created_after,
          createdBefore: filters.created_before,
          updatedAfter: filters.updated_after,
          updatedBefore: filters.updated_before,
          studentId: filters.student_id,
          status: filters.status,
        },
      )

      return unwrapStandardResponse<AttendanceListResponse>(
        response,
      ) as AttendanceListResponse
    },
    enabled:
      hasPermission("attendances:read") ||
      hasPermission("attendances:read_own") ||
      hasPermission("attendances:read_assigned"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single attendance by ID with permission checks
 */
export function useAttendance(attendanceId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["attendances", attendanceId],
    queryFn: async (): Promise<AttendanceResponse> => {
      const response =
        await api.attendances.readAttendanceApiV1AttendancesAttendanceIdGet({
          attendanceId: attendanceId,
        })

      return unwrapStandardResponse<AttendanceResponse>(
        response,
      ) as AttendanceResponse
    },
    enabled:
      !!attendanceId &&
      (hasPermission("attendances:read") ||
        hasPermission("attendances:read_own") ||
        hasPermission("attendances:read_assigned")),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new attendance with validation and error handling
 */
export function useCreateAttendance() {
  return useApiMutation<AttendanceResponse, unknown, AttendanceCreateInput>({
    mutationFn: async (attendanceData) => {
      // Let the backend handle permission checks

      const attendanceCreate: AttendanceCreate = {
        enrollment_id: attendanceData.enrollment_id,
        class_session_id: attendanceData.class_session_id,
        student_id: attendanceData.student_id,
        class_date: attendanceData.class_date,
        class_time: attendanceData.class_time ?? null,
        status: attendanceData.status ?? null,
        check_in_time: attendanceData.check_in_time ?? null,
        check_out_time: attendanceData.check_out_time ?? null,
        is_makeup_class: attendanceData.is_makeup_class ?? null,
        makeup_for_date: attendanceData.makeup_for_date ?? null,
        instructor_notes: attendanceData.instructor_notes ?? null,
        skills_practiced: attendanceData.skills_practiced ?? null,
        behavior_notes: attendanceData.behavior_notes ?? null,
        parent_notification_sent:
          attendanceData.parent_notification_sent ?? null,
        marked_by_user_id: attendanceData.marked_by_user_id ?? null,
        notes: attendanceData.notes ?? null,
      }

      const response =
        await api.attendances.createAttendanceApiV1AttendancesPost({
          requestBody: attendanceCreate,
        })

      return unwrapStandardResponse<AttendanceResponse>(
        response,
      ) as AttendanceResponse
    },
    invalidateQueries: [["attendances"]],
    successToast: (data) => ({
      title: "Attendance created successfully",
      description: ` "${data.id}" has been created.`,
    }),
  })
}

/**
 * Update existing attendance with validation and error handling
 */
export function useUpdateAttendance() {
  return useApiMutation<
    AttendanceResponse,
    unknown,
    { attendanceId: string; attendanceData: AttendanceUpdateInput }
  >({
    mutationFn: async ({
      attendanceId,
      attendanceData,
    }: {
      attendanceId: string
      attendanceData: AttendanceUpdateInput
    }) => {
      // Let the backend handle permission checks

      const attendanceUpdate: AttendanceUpdate = {
        enrollment_id: attendanceData.enrollment_id,
        class_session_id: attendanceData.class_session_id,
        student_id: attendanceData.student_id,
        class_date: attendanceData.class_date,
        class_time: attendanceData.class_time,
        status: attendanceData.status,
        check_in_time: attendanceData.check_in_time,
        check_out_time: attendanceData.check_out_time,
        is_makeup_class: attendanceData.is_makeup_class,
        makeup_for_date: attendanceData.makeup_for_date,
        instructor_notes: attendanceData.instructor_notes,
        skills_practiced: attendanceData.skills_practiced,
        behavior_notes: attendanceData.behavior_notes,
        parent_notification_sent: attendanceData.parent_notification_sent,
        marked_by_user_id: attendanceData.marked_by_user_id,
        notes: attendanceData.notes,
      }

      const response =
        await api.attendances.updateAttendanceApiV1AttendancesAttendanceIdPut({
          attendanceId: attendanceId,
          requestBody: attendanceUpdate,
        })

      return unwrapStandardResponse<AttendanceResponse>(
        response,
      ) as AttendanceResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["attendances", variables.attendanceId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["attendances"]],
  })
}

/**
 * Delete attendance with permission checks and error handling
 */
export function useDeleteAttendance() {
  return useApiMutation<{ attendanceId: string }, unknown, string>({
    mutationFn: async (attendanceId) => {
      // Let the backend handle permission checks
      await api.attendances.deleteAttendanceApiV1AttendancesAttendanceIdDelete({
        attendanceId: attendanceId,
      })
      return { attendanceId }
    },
    removeQueries: [(variables) => ["attendances", variables]],
    invalidateQueries: [["attendances"]],
  })
}

/**
 * Invalidate attendances queries to force a refetch
 */
export function useInvalidateAttendances() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["attendances"] })
  }
}
